import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Menu,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import FeatherIcon from "feather-icons-react";
import { useTranslation } from "react-i18next";

import { ColumnDefinition, TableFilter } from "../../EmployeeDataDrawer";
import { getColumnLabel } from "../../helpers";

type SortOrder = "alpha-asc" | "alpha-desc" | "num-asc" | "num-desc";

interface ColumnFilterMenuProps {
  anchorEl: HTMLElement | null;
  columnKey: string | null;
  columns: ColumnDefinition[];
  filterValue: string;
  setFilterValue: React.Dispatch<React.SetStateAction<string>>;
  uniqueValues: string[];
  filters: TableFilter[];
  onClose: () => void;
  onApplyFilter: () => void;
  onSelectValue: (value: string) => void;
  onClearColumnFilters: () => void;
}

export const ColumnFilterMenu: React.FC<ColumnFilterMenuProps> = ({
  anchorEl,
  columnKey,
  columns,
  filterValue,
  setFilterValue,
  uniqueValues,
  filters,
  onClose,
  onApplyFilter,
  onSelectValue,
  onClearColumnFilters,
}) => {
  const { t } = useTranslation();
  const [sortOrder, setSortOrder] = useState<SortOrder>("alpha-asc");

  // Check if current column has any filters
  const hasColumnFilters = filters.some((filter) => filter.columnKey === columnKey);

  // Sort the unique values based on selected sort order
  const sortedValues = useMemo(() => {
    const values = [...uniqueValues];

    switch (sortOrder) {
      case "alpha-asc":
        return values.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
      case "alpha-desc":
        return values.sort((a, b) => b.localeCompare(a, undefined, { sensitivity: "base" }));
      case "num-asc":
        return values.sort((a, b) => {
          const numA = parseFloat(a) || 0;
          const numB = parseFloat(b) || 0;

          return numA - numB;
        });
      case "num-desc":
        return values.sort((a, b) => {
          const numA = parseFloat(a) || 0;
          const numB = parseFloat(b) || 0;

          return numB - numA;
        });
      default:
        return values;
    }
  }, [uniqueValues, sortOrder]);

  const handleSortChange = (
    _event: React.MouseEvent<HTMLElement>,
    newSort: SortOrder | null,
  ): void => {
    if (newSort !== null) {
      setSortOrder(newSort);
    }
  };

  const handleClearFilters = (): void => {
    onClearColumnFilters();
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
    >
      <Box sx={{ p: 2, width: 320 }}>
        <Typography variant="subtitle2" gutterBottom>
          {columnKey
            ? getColumnLabel(columns, columnKey)
            : t("paygap.employeeData.filters.title")}
        </Typography>
        <TextField
          fullWidth
          size="small"
          label={t("paygap.employeeData.filters.value")}
          value={filterValue}
          onChange={(event): void => setFilterValue(event.target.value)}
          placeholder={t("paygap.employeeData.filters.placeholder")}
          sx={{ mb: 1 }}
        />
        <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
          <Button
            variant="contained"
            size="small"
            onClick={onApplyFilter}
            disabled={!columnKey || !filterValue.trim()}
            startIcon={<FeatherIcon icon="filter" size={14} />}
          >
            {t("paygap.employeeData.filters.add")}
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="error"
            onClick={handleClearFilters}
            disabled={!hasColumnFilters}
            startIcon={<FeatherIcon icon="x" size={14} />}
          >
            {t("paygap.employeeData.filters.clear")}
          </Button>
        </Stack>

        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
          <Typography variant="caption" color="textSecondary">
            {t("paygap.employeeData.filters.values")}
          </Typography>
          <ToggleButtonGroup
            value={sortOrder}
            exclusive
            onChange={handleSortChange}
            size="small"
            sx={{ height: 24 }}
          >
            <ToggleButton value="alpha-asc" sx={{ px: 0.75, py: 0 }}>
              <Tooltip title={t("paygap.employeeData.filters.sortAlphaAsc", "A-Z")}>
                <Box display="flex" alignItems="center" gap={0.25}>
                  <Typography variant="caption" sx={{ fontSize: "0.65rem", fontWeight: 600 }}>{"AZ"}</Typography>
                  <FeatherIcon icon="arrow-up" size={10} />
                </Box>
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="alpha-desc" sx={{ px: 0.75, py: 0 }}>
              <Tooltip title={t("paygap.employeeData.filters.sortAlphaDesc", "Z-A")}>
                <Box display="flex" alignItems="center" gap={0.25}>
                  <Typography variant="caption" sx={{ fontSize: "0.65rem", fontWeight: 600 }}>{"ZA"}</Typography>
                  <FeatherIcon icon="arrow-down" size={10} />
                </Box>
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="num-asc" sx={{ px: 0.75, py: 0 }}>
              <Tooltip title={t("paygap.employeeData.filters.sortNumAsc", "0-9")}>
                <Box display="flex" alignItems="center" gap={0.25}>
                  <Typography variant="caption" sx={{ fontSize: "0.65rem", fontWeight: 600 }}>{"09"}</Typography>
                  <FeatherIcon icon="arrow-up" size={10} />
                </Box>
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="num-desc" sx={{ px: 0.75, py: 0 }}>
              <Tooltip title={t("paygap.employeeData.filters.sortNumDesc", "9-0")}>
                <Box display="flex" alignItems="center" gap={0.25}>
                  <Typography variant="caption" sx={{ fontSize: "0.65rem", fontWeight: 600 }}>{"90"}</Typography>
                  <FeatherIcon icon="arrow-down" size={10} />
                </Box>
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Box
          sx={{
            maxHeight: 240,
            overflowY: "auto",
            mt: 0.5,
            border: "1px solid #eee",
            borderRadius: 1,
          }}
        >
          {sortedValues.map((value) => (
            <MenuItem key={value} onClick={(): void => onSelectValue(value)} dense>
              <Checkbox
                size="small"
                checked={filters.some(
                  (filter) =>
                    filter.columnKey === columnKey &&
                    filter.value.toLowerCase() === value.toLowerCase(),
                )}
                tabIndex={-1}
                disableRipple
              />
              <Typography variant="body2">{value}</Typography>
            </MenuItem>
          ))}
          {sortedValues.length === 0 && (
            <MenuItem disabled>{t("paygap.employeeData.filters.noValues")}</MenuItem>
          )}
        </Box>
      </Box>
    </Menu>
  );
};
